/* eslint-disable no-undef */

import React, {Component} from 'react'
import {withStyles} from "@material-ui/core/styles/index";
import MailCheck from 'react-mailcheck'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {
    Grid,
    TextField,
    FormHelperText,
    Link,
    Typography,
    InputAdornment,
    CircularProgress,
    Box
} from '@material-ui/core';

// Base styles (theme-based, for all layouts except 'full')
const baseStyles = theme => ({
    suggestion: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        textAlign: 'center',
    },
    suggestionLink: {
        cursor: 'pointer',
    },
    errorText: {
        marginTop: theme.spacing(0.8),
        color: theme.palette.error.main + '!important',
        fontSize: '0.9rem!important',
        textAlign: theme.errorText && theme.errorText.textAlign ? theme.errorText.textAlign : 'center!important',
    },
    loadingIcon: {
        width: 20 + 'px!important',
        height: 20 + 'px!important',
        marginRight: 14,
        marginLeft: 6,
        color: theme.palette.primaryColor,
    },
    questionWrapper: {
        marginTop: '0px!important'
    },
    [theme.breakpoints.down(600)]: {
        questionWrapper: {
            padding: '4px!important'
        }
    },
    textField: {
        '& fieldset': {
            border: theme.input ? theme.input.border + '!important' : null
        }
    },
    formHelperText: {
        color: 'rgba(0, 0, 0, 0.81)',
        lineHeight: 1.2,
        padding: theme.spacing(0.5, 0.5, 0, 0.5),
        fontSize: '0.8rem',
        marginBottom: theme.spacing(-0.5),
    },
    outlinedTextField: {
        '& input': {
            background: '#fff',
        },
    },
    hasAdornedEnd: {
        '> div': {
            paddingRight: 0
        }
    },
    iconWrapper: {
        height: 50,
        padding: '0 14px',
        display: 'flex',
        alignItems: 'center',
        background: '#f9f9f9',
        position: 'absolute',
        right: 0,
    }
});

// Enhanced styles (for layout='full' only)
const enhancedStyles = theme => ({
    suggestion: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        textAlign: 'center',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontSize: '0.9rem',
        color: '#4a5d52',
    },
    suggestionLink: {
        cursor: 'pointer',
        color: '#205a40',
        fontWeight: 600,
    },
    errorText: {
        marginTop: theme.spacing(0.8),
        color: '#dc2626!important',
        fontSize: '0.875rem!important',
        textAlign: 'left!important',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    loadingIcon: {
        width: 20 + 'px!important',
        height: 20 + 'px!important',
        marginRight: 14,
        marginLeft: 6,
        color: '#205a40',
    },
    questionWrapper: {
        marginTop: '0px!important'
    },
    [theme.breakpoints.down(600)]: {
        questionWrapper: {
            padding: '4px!important'
        }
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontSize: '1rem',
            background: '#ffffff',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            '& fieldset': {
                borderColor: 'rgba(32, 90, 64, 0.2)',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(32, 90, 64, 0.4)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#205a40',
                borderWidth: '2px',
            },
            '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(32, 90, 64, 0.1)',
            },
        },
        '& .MuiInputLabel-root': {
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontSize: '0.95rem',
            color: '#6a7d72',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#205a40',
            fontWeight: 600,
        },
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
        },
    },
    formHelperText: {
        color: '#4a5d52',
        lineHeight: 1.4,
        padding: theme.spacing(0.75, 0.5, 0, 0.5),
        fontSize: '0.8rem',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        marginBottom: theme.spacing(-0.5),
    },
    outlinedTextField: {
        '& input': {
            background: '#fff',
        },
    },
    hasAdornedEnd: {
        '> div': {
            paddingRight: 0
        }
    },
    iconWrapper: {
        height: 50,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, rgba(32, 90, 64, 0.05) 0%, rgba(32, 90, 64, 0.1) 100%)',
        position: 'absolute',
        right: 0,
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        color: '#205a40',
    }
});

const domains = ['gmail.com', 'hotmail.com', 'luukku.com', 'outlook.com', 'suomi24.fi', 'hotmail.fi', 'elisanet.fi', 'pp.inet.fi', 'yahoo.com', 'kolumbus.fi', 'icloud.com', 'netti.fi', 'dnainternet.net', 'windowslive.com', 'saunalahti.fi', 'netikka.fi', 'live.fi', 'mail.com', 'pp1.inet.fi', 'msn.com', 'wippies.fi', 'phnet.fi', 'live.com', 'welho.com', 'jippii.fi', 'kotinet.com', 'kymp.net', 'meiliboxi.fi', 'hot.ee', 'wippies.com', 'pp2.inet.fi', 'gumpler.com', 'mail.ru', 'fimnet.fi', 'yahoo.fi', 'me.com', 'nic.fi', 'te-toimisto.fi', 'iki.fi', 'paltamo.net', 'dekra.com', 'hel.fi', 'mail.ee', 'kopteri.net', 'pp3.inet.fi', 'pp4.inet.fi', 'multi.fi', 'hus.fi', 'helsinkilainen.com', 'kotikone.fi', 'evl.fi', 'helsinki.fi', 'anvianet.fi', 'pp.nic.fi', 'sci.fi', 'lailanet.fi', 'mail.suomi.net', 'kajaani.net', 'mbnet.fi', 'tts.fi', 'edu.hel.fi', 'utu.fi', 'surffi.fi', 'liikennekoulukaakinen.fi', 'kotiportti.fi', 'phpoint.fi', 'yahoo.co.uk', 'kotiposti.net', 'vuotila.com', 'pp5.inet.fi', 'sulo.fi', 'yandex.ru', 'espoo.fi', 'kemi.fi', 'rocketmail.com', 'suursaimaa.com', 'tpnet.fi', 'rokki.net', 'seutuposti.fi', 'turku.fi', 'yahoo.se', 'eduouka.fi', 'japo.fi', 'hotmail.se', 'koivurinne.fi', 'surfnet.fi', 'telemail.fi', 'vantaa.fi', 'omanetti.fi', 'pp.phnet.fi', 'surffi.net', 'aalto.fi', 'draivimedia.fi', 'pispala.net', 'dlc.fi', 'haminetti.net', 'protonmail.com', 'salo.fi', 'edu.vaasa.fi', 'jkl.fi', 'uef.fi', 'aol.com', 'ippnet.fi', 'aland.net', 'kase.fi', 'meili.fi', 'bastu.net', 'gmx.com', 'kotka.fi', 'live.se', 'wmail.fi', 'rambler.ru', 'op.fi', 'vaasa.fi', 'yandex.com', '2me.fi', 'co.inet.fi', 'raahe.fi', 'pp6.inet.fi', 'abo.fi', 'aol.fi', 'posti24.fi', 'tampere.fi', 'vero.fi', 'koti.luukku.com', 'lappee.fi', 'neste.com', 'edu.lahti.fi', 'edukemi.fi', 'ovi.com', 'roiedu.fi', 'tampereensyke.fi', 'wiksu.fi', 'ylojarvi.fi', 'oyk.fi', 'edu.lohja.fi', 'leadspark.io', 'ppa.inet.fi', 'brev.fi', 'luukk.com', 'posti.com', 'rauma.fi', 'lavikko.com', 'pshp.fi', 'tapas.fi', 'abv.bg', 'hukassa.com', 'kuh.fi', 'nettikirje.fi', 'pori.fi', 'saunis.net', 'sok.fi', 'ouka.fi', 'pp7.inet.fi', 'ppd.inet.fi', 'rousku.net', 'rovaniemi.fi', 'sedu.fi', 'seinajoki.fi', 'suomi.fi', 'tyks.fi', 'uta.fi', 'ymail.com', 'lempaala.fi', 'seppotossavainen.fi', 'yit.fi', 'yle.fi', 'kainuu.fi', 'khshp.fi', 'koti.fimnet.fi', 'kuopionkonservatorio.fi', 'openmarket.fi', 'riihimaki.fi', 'student.jyu.fi', 'yahoo.es', 'yahoo.fr', 'edupori.fi', 'hotmail.co.uk', 'inbox.ru', 'kuopio.fi', 'lahtienergia.fi', 'list.ru', 'luukku.fi', 'eduvantaa.fi', 'incrosnatu.fi', 'kouvola.fi', 'kuhmo.net', 'saunassa.com', 'suomi.24.fi', 'tieto.com', 'arabianranta.com', 'hotmail.fr', 'hotmail.it', 'jyu.fi', 'kesko.fi', 'lannenlaatuikkuna.fi', 'nostemedia.fi', 'paimiolainen.com', 'porvoo.fi', 'terveystalo.com', 'upm.com', 'wippies.ee', 'ecex.fi', 'inbox.lv', 'jamsa.fi', 'msoynet.com', 'parnet.fi', 'pp8.inet.fi', 'putula.com', 'rahti-kujala.com', 'twinspark.fi', 'arkea.fi', 'jarvensivu.com', 'kiinteistomaailma.fi', 'lakiasiasi.fi', 'luke.fi', 'mehilainen.fi', 'metropolia.fi', 'metsa.fi', 'mynamaki.fi', 'nokia.com', 'nordea.com', 'novart.fi', 'oulu.fi', 'telia.com', 'tervo.fi', 'valmet.com', 'crefisol.com', 'ehox.fi', 'ely-keskus.fi', 'globalbar.fi', 'huittinen.fi', 'iss.fi', 'jacon.fi', 'mac.com', 'online.ee', 'opaasi.fi', 'samlink.fi', 'siriocomp.com', 'storaenso.com', 'trafi.fi', '703gamli.com', 'ads.fi', 'attendo.fi', 'caverion.com', 'edu.espoo.fi', 'elisa.fi', 'hekula.net', 'jya.fi', 'nettilinja.fi', 'oikeus.fi', 'outlook.fi', 'pjhoy.fi', 'pp.htv.fi', 'rakennuskonsultit.fi', 'sasky.fi', 'siltala.net', 'ssvnet.fi', 'suomi24.com', 'turkuamk.fi', 'webmail.net', 'ypaja.fi', '24.fi', 'delete.fi', 'dhl.com', 'edukouvola.fi', 'hyl.fi', 'internet.net', 'kaarina.fi', 'keka.fi', 'koulut.kaarina.fi', 'kuntaliitto.fi', 'lapinkumi.fi', 'lappeenranta.fi', 'myhome.fi', 'netlife.fi', 'nokiankaupunki.fi', 'od.fi', 'oulunkaari.com', 'quicknet.inet.fi', 'ras.fi', 'tapkoy.fi', 'tintti.net', 'tuni.fi', 'viitasaari.fi', 'vr.fi'];

const secondLevelDomains = ['hotmail']
const topLevelDomains = ['com', 'net', 'org', 'fi']

class EmailQuestion extends Component {
    state = {}

    updateValue = (value) => {
        this.props.onValueChanged(this.props.question.id, value)
    };


    handleChange = (event) => {
        this.props.onValueChanged(this.props.question.id, event.target.value)
    };

    render() {
        const {classes, question, value, theme, layout} = this.props;
        const inputValue = value ? value : '';
        if (question.waitingValidation === true) {
            question.valid = true
        }

        let showValidationIcon = false
        showValidationIcon = question.waitingValidation

        return (
            <Grid item xs={parseInt(question.xsCols)} sm={parseInt(question.smCols)}
                  className={classes.questionWrapper}>
                <MailCheck email={value ? value : ''} domains={domains} secondLevelDomains={secondLevelDomains}
                           topLevelDomains={topLevelDomains}>
                    {suggestion => (<>
                        <TextField required label={question.title} name={question.name} fullWidth
                                   variant={theme.inputVariant}
                                   autoFocus={question.autoFocus} onChange={this.handleChange}
                                   error={question.valid === false} value={inputValue}
                                   InputProps={{
                                       endAdornment: <>{showValidationIcon === true ?
                                           <InputAdornment position="end"><CircularProgress
                                               className={classes.loadingIcon}/></InputAdornment> :
                                           <Box className={classes.iconWrapper}><FontAwesomeIcon icon={['fal', 'envelope']}/></Box>}</>
                                   }}
                                   className={classes.textField + ' ' + (theme.inputVariant === 'outlined' ? classes.outlinedTextField : null) + ' inputText emailInputText'}/>
                        {suggestion &&
                        <Typography variant="body2" className={classes.suggestion}>Tarkoititko <Link
                            onClick={() => this.updateValue(suggestion.full)}
                            className={classes.suggestionLink}>{suggestion.full}</Link>?</Typography>}
                        {question.description && <FormHelperText className={classes.formHelperText}>{question.description}</FormHelperText>}
                    </>)}
                </MailCheck>
                {question.valid === false &&
                <Grid item xs={12} style={{paddingTop: 0}}><FormHelperText
                    className={classes.errorText}>{question.errorMessage}</FormHelperText></Grid>}
            </Grid>
        )
    }
}

// Pre-create styled components at module level to prevent re-renders
const EmailQuestionBase = withStyles(baseStyles)(EmailQuestion);
const EmailQuestionEnhanced = withStyles(enhancedStyles)(EmailQuestion);

// Style switcher - returns pre-created components to prevent re-mounting
const EmailQuestionWithStyles = (props) => {
    const { layout, ...rest } = props;
    
    if (layout === 'full') {
        return <EmailQuestionEnhanced {...props} />;
    }
    return <EmailQuestionBase {...props} />;
};

export default EmailQuestionWithStyles;
